/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: api/timeConditionApi.ts
 🗓️ Created: 2025-01-15
 🧩 Description:
    API functions for Time Conditions.
    Centralizes all API calls related to Time Conditions.
────────────────────────────────────────────────────────────────*/

import { buildApiUrl, getApiHeaders } from '../../../config/api';
import {
  TimeCondition,
  TimeConditionPayload,
  ListTimeConditionsResponse,
  TimeConditionResponse,
} from '../types';

export const TIME_CONDITIONS_ENDPOINT = '/api/telephony/time-conditions';

/**
 * Generic API request helper
 */
async function requestTimeConditionsApi<T>(
  endpoint: string,
  errorMessage: string,
  options?: RequestInit,
): Promise<{ data: T }> {
  const response = await fetch(buildApiUrl(endpoint), {
    ...options,
    headers: {
      ...getApiHeaders(),
      ...(options?.headers || {}),
    },
  });

  if (!response.ok) {
    let message = errorMessage;
    try {
      const errorData = await response.json();
      if (errorData?.message) {
        message = errorData.message;
      }
    } catch {
      // Ignore JSON parse errors
    }
    throw new Error(message);
  }

  const result = await response.json();
  if (!result?.success) {
    throw new Error(result?.message || errorMessage);
  }

  return { data: result.data };
}

/**
 * List Time Conditions with pagination and search
 */
export async function fetchTimeConditions(
  pagination: {
    page: number;
    limit: number;
    sortBy: string;
    sortOrder: 'asc' | 'desc';
    domainId?: number;
  },
  search?: string,
  sortBy?: string,
): Promise<ListTimeConditionsResponse> {
  const queryParams = new URLSearchParams({
    page: pagination.page.toString(),
    limit: pagination.limit.toString(),
    sortBy: sortBy || pagination.sortBy,
    sortOrder: pagination.sortOrder,
  });

  if (search) {
    queryParams.append('search', search);
  }

  if (pagination.domainId != null && !Number.isNaN(Number(pagination.domainId))) {
    queryParams.append('domainId', pagination.domainId.toString());
  }

  const response = await fetch(
    buildApiUrl(`${TIME_CONDITIONS_ENDPOINT}?${queryParams}`),
    {
      headers: getApiHeaders(),
    },
  );

  if (!response.ok) {
    throw new Error('Failed to fetch time conditions');
  }

  const result = await response.json();

  if (!result?.success || !Array.isArray(result.data)) {
    throw new Error(result?.message || 'Invalid response');
  }

  return {
    success: true,
    data: result.data as TimeCondition[],
    pagination: {
      page: result.pagination?.page || pagination.page,
      limit: result.pagination?.limit || pagination.limit,
      total: result.pagination?.total || 0,
      totalPages: result.pagination?.totalPages || 0,
    },
  };
}

/**
 * Get single Time Condition by ID
 */
export async function fetchTimeCondition(id: string | number): Promise<TimeCondition> {
  const { data } = await requestTimeConditionsApi<TimeCondition>(
    `${TIME_CONDITIONS_ENDPOINT}/${id}`,
    'Failed to fetch time condition',
  );
  return data;
}

/**
 * Create new Time Condition
 */
export async function createTimeCondition(
  payload: TimeConditionPayload,
): Promise<TimeCondition> {
  const { data } = await requestTimeConditionsApi<TimeCondition>(
    TIME_CONDITIONS_ENDPOINT,
    'Failed to create time condition',
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    },
  );
  return data;
}

/**
 * Update Time Condition
 */
export async function updateTimeCondition(
  id: string | number,
  payload: TimeConditionPayload,
): Promise<TimeCondition> {
  const { data } = await requestTimeConditionsApi<TimeCondition>(
    `${TIME_CONDITIONS_ENDPOINT}/${id}`,
    'Failed to update time condition',
    {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    },
  );
  return data;
}

/**
 * Delete Time Condition
 */
export async function deleteTimeCondition(id: string | number): Promise<void> {
  await requestTimeConditionsApi<null>(
    `${TIME_CONDITIONS_ENDPOINT}/${id}`,
    'Failed to delete time condition',
    {
      method: 'DELETE',
    },
  );
}

